import React, { useEffect, useState, useContext } from "react";
import { formatPhone } from "../../utils/formatPhone";
import { AuthContext } from "../../contexts/AuthContext";
import secureAxios from "../../utils/secureAxios";

const CreateCounselorForm = ({ onCreated }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    rsa_email: "",
    phone_number: "",
    customer_id: "",
    status_id: "",
    is_existing: false,
    existing_counselor_id: "",
    branch_id: "", // Changed from branch text to branch_id
  });

  const [customers, setCustomers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [availableCounselors, setAvailableCounselors] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { user, tenant_id } = useContext(AuthContext);

  useEffect(() => {
    if (!tenant_id) return;

    // Load customers
    secureAxios
      .get(`/api/customers?tenant_id=${tenant_id}`)
      .then((res) => {
        setCustomers(res.data);
      })
      .catch((err) => {
        console.error("Error loading customers:", err.message);
        setMessage("Error loading customers: " + err.message);
      });

    // Load statuses
    secureAxios
      .get(`/api/status?tenant_id=${tenant_id}&type=Counselor`)
      .then((res) => {
        setStatuses(res.data);
      })
      .catch((err) => {
        console.error("Error loading statuses:", err.message);
        setMessage("Error loading statuses: " + err.message);
      });

    // Load branches for dropdown
    secureAxios
      .get(`/api/branches/dropdown?tenant_id=${tenant_id}`)
      .then((res) => {
        setBranches(res.data);
      })
      .catch((err) => {
        console.error("Error loading branches:", err.message);
        // Non-critical - branches are optional
      });
  }, [tenant_id]);

  // Filter branches when customer changes
  useEffect(() => {
    if (!tenant_id) return;

    let url = `/api/branches/dropdown?tenant_id=${tenant_id}`;
    if (formData.customer_id) {
      url += `&customer_id=${formData.customer_id}`;
    }

    secureAxios
      .get(url)
      .then((res) => {
        setBranches(res.data);
        // Clear branch selection if current branch not in filtered list
        if (
          formData.branch_id &&
          !res.data.some((b) => b.branch_id === formData.branch_id)
        ) {
          setFormData((prev) => ({ ...prev, branch_id: "" }));
        }
      })
      .catch((err) => {
        console.error("Error loading branches:", err.message);
      });
  }, [formData.customer_id, tenant_id]);

  useEffect(() => {
    if (formData.customer_id && formData.is_existing) {
      fetchAvailableCounselors(formData.customer_id, tenant_id);
    } else {
      setAvailableCounselors([]);
    }
  }, [formData.customer_id, formData.is_existing, tenant_id]);

  const fetchAvailableCounselors = async (customerId, currentTenantId) => {
    try {
      const res = await secureAxios.get(`/api/counselors/available`, {
        params: {
          customer_tenant_id: customerId,
          current_tenant_id: currentTenantId,
        },
      });
      const filtered = res.data.filter(
        (c) => !c.linked_tenants?.includes(currentTenantId)
      );
      setAvailableCounselors(filtered);
    } catch (err) {
      console.error("Failed to fetch available counselors:", err.message);
      setMessage("Error loading available counselors: " + err.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue = name === "phone_number" ? value.replace(/\D/g, "") : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("Submitting...");
    setLoading(true);

    try {
      if (formData.is_existing) {
        if (
          !formData.existing_counselor_id ||
          formData.existing_counselor_id.length !== 36
        ) {
          setMessage("Please select a valid existing counselor.");
          setLoading(false);
          return;
        }

        if (!formData.customer_id) {
          setMessage("Please select a customer.");
          setLoading(false);
          return;
        }

        const payload = {
          tenant_id,
          user_id: formData.existing_counselor_id,
          customer_id: formData.customer_id,
          created_by: user?.user_id,
        };

        await secureAxios.post(`/api/counselors/link`, payload);
        setMessage("Counselor linked successfully!");
      } else {
        if (
          !formData.name ||
          !formData.email ||
          !formData.phone_number ||
          !formData.status_id ||
          !formData.customer_id
        ) {
          setMessage("Please fill in all required fields.");
          setLoading(false);
          return;
        }

        const payload = {
          tenant_id,
          name: formData.name,
          email: formData.email,
          phone_number: formData.phone_number,
          status_id: formData.status_id,
          customer_id: formData.customer_id,
          branch_id: formData.branch_id || null, // Send branch_id instead of branch text
          created_by: user?.user_id,
        };

        await secureAxios.post(`/api/counselors`, payload);
        setMessage("Counselor created successfully!");
      }

      if (onCreated) onCreated();

      setFormData({
        name: "",
        email: "",
        phone_number: "",
        status_id: "",
        customer_id: "",
        is_existing: false,
        existing_counselor_id: "",
        branch_id: "",
      });
      setAvailableCounselors([]);
    } catch (err) {
      console.error("Error submitting:", err.message);
      const backendMessage =
        err?.response?.data?.error || "Unknown server error.";
      setMessage(`Server error: ${backendMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {message && (
        <div
          className={`alert ${
            message.includes("successfully")
              ? "alert-success"
              : message.includes("error") || message.includes("Error")
              ? "alert-danger"
              : "alert-info"
          }`}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        <div className="col-12">
          <div className="form-check mb-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="is_existing"
              checked={formData.is_existing}
              onChange={(e) => {
                const is_existing = e.target.checked;
                setFormData((prev) => ({
                  ...prev,
                  is_existing,
                  existing_counselor_id: "",
                }));

                if (is_existing && formData.customer_id) {
                  fetchAvailableCounselors(formData.customer_id, tenant_id);
                } else {
                  setAvailableCounselors([]);
                }
              }}
            />
            <label className="form-check-label" htmlFor="is_existing">
              Link Existing Counselor?
            </label>
          </div>
        </div>

        <div className="col-md-6">
          <label className="form-label">Customer</label>
          <select
            className="form-select"
            name="customer_id"
            value={formData.customer_id}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select Customer</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {formData.is_existing && availableCounselors.length > 0 && (
          <div className="col-md-6">
            <label className="form-label">Select Existing Counselor</label>
            <select
              className="form-select"
              name="existing_counselor_id"
              value={formData.existing_counselor_id}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">-- Select Counselor --</option>
              {availableCounselors.map((c) => (
                <option key={c.user_id} value={c.user_id}>
                  {c.name} ({c.email})
                </option>
              ))}
            </select>
          </div>
        )}

        {!formData.is_existing && (
          <>
            <div className="col-md-6">
              <label className="form-label">Name *</label>
              <input
                className="form-control"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={loading}
                autoComplete="name"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Email *</label>
              <input
                className="form-control"
                name="email"
                value={formData.email}
                onChange={handleChange}
                type="email"
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Additional Email (RSA)
                <i
                  className="bi bi-info-circle ms-1 text-muted"
                  title="RSA email will auto-populate in Email Document Bundle"
                  style={{ cursor: "help" }}
                ></i>
              </label>
              <input
                className="form-control"
                name="rsa_email"
                value={formData.rsa_email}
                onChange={handleChange}
                type="email"
                disabled={loading}
                autoComplete="email"
                placeholder="rsa@example.com"
              />
              <div className="form-text">
                Auto-populates "Additional Recipients" when emailing documents
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Phone Number *</label>
              <input
                className="form-control"
                name="phone_number"
                value={formatPhone(formData.phone_number)}
                onChange={handleChange}
                required
                disabled={loading}
                autoComplete="tel"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Status *</label>
              <select
                className="form-select"
                name="status_id"
                value={formData.status_id}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="">Select Status</option>
                {statuses.map((s) => (
                  <option key={s.status_id} value={s.status_id}>
                    {s.status}
                  </option>
                ))}
              </select>
            </div>

            {/* Branch/Office Dropdown */}
            <div className="col-md-6">
              <label className="form-label">
                Branch/Office
                <i
                  className="bi bi-info-circle ms-1 text-muted"
                  title="Used on invoice billing address"
                  style={{ cursor: "help" }}
                ></i>
              </label>
              <select
                className="form-select"
                name="branch_id"
                value={formData.branch_id}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="">-- Select Branch/Office --</option>
                {branches.map((b) => (
                  <option key={b.branch_id} value={b.branch_id}>
                    {b.branch_name}
                  </option>
                ))}
              </select>
              <div className="form-text">
                Branch or office name displayed on invoice "Bill To" section
              </div>
            </div>
          </>
        )}

        <div className="col-12">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Processing...
              </>
            ) : formData.is_existing ? (
              "Link Counselor"
            ) : (
              "Create Counselor"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCounselorForm;